import { Groups } from "@mui/icons-material";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import MuiAutocompleteMultiSelect from "@/components/common/MuiAutocompleteMultiSelect";
import { ComparisonPageHeaderShell } from "@/components/common/ReportingPageHeaderShell";
import { type AggregateData, AnalyticsServiceV2 } from "@/services/features/AnalyticsServiceV2";
import { type Store, StoreService } from "@/services/features/StoreService";
import { formatDurationShort } from "@/utils/time";

interface WeekKey {
  start: string;
  end: string;
  key: string; // `${start}|${end}`
}

function toDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatToMMDD(yyyyMmDd: string): string {
  // Expecting yyyy-MM-dd
  if (!yyyyMmDd || yyyyMmDd.length < 10) return yyyyMmDd;
  const [, m, d] = yyyyMmDd.split("-");
  return `${m}/${d}`;
}

function todayStr(): string {
  return toDateString(new Date());
}

function clampEndToToday(end: string): string {
  const today = todayStr();
  // ISO yyyy-MM-dd compares lexicographically
  return end > today ? today : end;
}

function formatMsToMinSec(ms: number, locale?: string): string {
  if (!Number.isFinite(ms) || ms < 0) return "-";
  return formatDurationShort(ms / 1000, locale);
}

function getLastNWeeksRange(n: number): { startDate: string; endDate: string } {
  const end = new Date();
  const start = new Date();
  // Roughly last n weeks back from today
  start.setDate(start.getDate() - 7 * n);
  return { startDate: toDateString(start), endDate: toDateString(end) };
}

const ComparisonAnalytics: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string | null>(null);
  const [stores, setStores] = React.useState<Store[]>([]);
  const [weeklyData, setWeeklyData] = React.useState<
    Record<
      string,
      AggregateData<{
        totalSessions?: number;
        totalItems?: number;
        totalSessionValue?: number;
        numberOfEngagedShoppers?: number;
        totalDwellMillisecond?: number;
      }>[]
    >
  >({});

  type MetricOption =
    | "sessionCounts"
    | "unitsPerSession"
    | "sessionValue"
    | "dwellTime"
    | "engagedShoppers";

  const metricOptions: ReadonlyArray<{ value: MetricOption; label: string }> = [
    { value: "sessionCounts", label: t("comparisonAnalytics.metrics.sessionCounts") },
    { value: "unitsPerSession", label: t("comparisonAnalytics.metrics.unitsPerSession") },
    { value: "sessionValue", label: t("comparisonAnalytics.metrics.sessionValue") },
    { value: "dwellTime", label: t("comparisonAnalytics.metrics.dwellTime") },
    { value: "engagedShoppers", label: t("comparisonAnalytics.metrics.engagedShoppers") },
  ];

  const [metric, setMetric] = React.useState<MetricOption>("sessionCounts");

  const storeService = React.useMemo(() => StoreService.getInstance(), []);
  const analyticsService = React.useMemo(() => AnalyticsServiceV2.getInstance(), []);

  React.useEffect(() => {
    let isMounted = true;
    async function load() {
      try {
        setLoading(true);
        setError(null);

        // Fetch all stores
        const storeResp = await storeService.getStores();
        const allStores = storeResp.data || [];
        if (!isMounted) return;
        setStores(allStores);

        const storeIds = allStores.map((s) => s.id);
        if (storeIds.length === 0) {
          setWeeklyData({});
          return;
        }

        // Last 5 weeks range
        const { startDate, endDate } = getLastNWeeksRange(5);

        const resp = await analyticsService.getSessionStatsByWeek({
          storeIds,
          startDate,
          endDate,
        });

        if (!isMounted) return;
        setWeeklyData(resp.data || {});
      } catch (e: any) {
        if (!isMounted) return;
        setError(e?.message || t("comparisonAnalytics.errors.failedLoadAnalytics"));
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    load();
    return () => {
      isMounted = false;
    };
  }, [storeService, analyticsService, t]);

  // Build week columns from response data
  const weekColumns: WeekKey[] = React.useMemo(() => {
    const set = new Map<string, WeekKey>();
    Object.values(weeklyData).forEach((list) => {
      (list || []).forEach((agg) => {
        const key = `${agg.start}|${agg.end}`;
        if (!set.has(key)) {
          set.set(key, { start: agg.start, end: agg.end, key });
        }
      });
    });
    const arr = Array.from(set.values());
    // Sort ascending by start date
    arr.sort((a, b) => (a.start < b.start ? -1 : a.start > b.start ? 1 : 0));
    return arr;
  }, [weeklyData]);

  const content = React.useMemo(() => {
    if (loading) {
      return null;
    }
    if (error) {
      return (
        <Box color="error.main" component="p" sx={{ typography: "body2", m: 0 }}>
          {error}
        </Box>
      );
    }
    if (!stores.length) {
      return (
        <Box component="p" sx={{ typography: "body2", m: 0 }}>
          {t("comparisonAnalytics.empty.noStoresFound")}
        </Box>
      );
    }
    return (
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("comparisonAnalytics.columns.store")}</TableCell>
              {weekColumns.map((w) => {
                const displayEnd = clampEndToToday(w.end);
                return (
                  <TableCell key={w.key} align="right">
                    {formatToMMDD(w.start)} - {formatToMMDD(displayEnd)}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {stores.map((store) => {
              const storeWeeks = weeklyData?.[String(store.id)] || [];
              return (
                <TableRow key={store.id} hover>
                  <TableCell component="th" scope="row">
                    {store.name}
                  </TableCell>
                  {weekColumns.map((wk) => {
                    const match = storeWeeks.find(
                      (it) => it.start === wk.start && it.end === wk.end,
                    );
                    let display: string | number = "-";
                    if (match?.data) {
                      const d = match.data;
                      switch (metric) {
                        case "sessionCounts":
                          if (d.totalSessions != null) display = d.totalSessions;
                          break;
                        case "unitsPerSession": {
                          const denom = d.totalSessions ?? 0;
                          const numer = d.totalItems ?? 0;
                          if (denom > 0) display = (numer / denom).toFixed(2);
                          break;
                        }
                        case "sessionValue":
                          if (d.totalSessionValue != null) {
                            const rounded = Math.round(d.totalSessionValue);
                            const formatted = rounded.toLocaleString();
                            const symbol = store.currencySymbol || "";
                            display = symbol ? `${symbol}${formatted}` : formatted;
                          }
                          break;
                        case "dwellTime": {
                          const denom = d.totalSessions ?? 0;
                          const numer = d.totalDwellMillisecond ?? 0;
                          if (denom > 0) {
                            const avgMs = numer / denom;
                            display = formatMsToMinSec(
                              avgMs,
                              i18n.resolvedLanguage || i18n.language,
                            );
                          }
                          break;
                        }
                        case "engagedShoppers":
                          if (d.numberOfEngagedShoppers != null)
                            display = d.numberOfEngagedShoppers;
                          break;
                        default:
                          break;
                      }
                    }
                    return (
                      <TableCell key={wk.key} align="right">
                        {display}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }, [
    loading,
    error,
    stores,
    weekColumns,
    weeklyData,
    metric,
    i18n.resolvedLanguage,
    i18n.language,
    t,
  ]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <ComparisonPageHeaderShell
        title={t("comparisonAnalytics.title")}
        gradientTitle
        icon={<Groups />}
        loading={loading}
      />
      <Box sx={{ px: 2, display: "flex", gap: 2 }}>
        <MuiAutocompleteMultiSelect
          sx={{ minWidth: 240 }}
          size="small"
          label={t("comparisonAnalytics.fields.metric")}
          options={metricOptions}
          value={metricOptions.filter((option) => option.value === metric)}
          onChange={(next) => {
            const selectedMetric = next[0];
            if (selectedMetric) {
              setMetric(selectedMetric.value);
            }
          }}
          getOptionLabel={(option) => option.label}
          getOptionKey={(option) => option.value}
        />
      </Box>
      <Box sx={{ px: 2, pb: 2 }}>{content}</Box>
    </Box>
  );
};

export default ComparisonAnalytics;
